import React from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import './StatusMenu.scss';
import { statuses } from 'constants/statuses';
import schoolsService from 'services/schools.service';
import Chip from '@material-ui/core/Chip';
import { Spinner } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';

export default function StatusMenu({ school, currentStatus, onUpdated }) {
  const queryClient = useQueryClient();
  const isCollege = school.organization_type === 'college';
  const options = statuses.filter(
    (option) => !isCollege || option.id !== 'waitlist',
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedStatus, setSelectedStatus] = React.useState(currentStatus);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => setSelectedStatus(currentStatus), [currentStatus]);

  const handleMenuItemClick = async (status) => {
    setAnchorEl(null);
    if (status === selectedStatus) return;
    setError('');
    setLoading(true);
    try {
      const service = schoolsService();
      const update = isCollege ? service.updateCollege : service.updateSchool;
      await update(school.school_name, { status });
      setSelectedStatus(status);
      await Promise.all([
        queryClient.invalidateQueries(['schools']),
        queryClient.invalidateQueries(['school', school.school_name]),
        queryClient.invalidateQueries(['schoolInfo', school.school_name]),
      ]);
      onUpdated?.();
    } catch {
      setError('Could not update status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const status = statuses.find((status) => status.id === selectedStatus);

  return (
    <div>
      <div className="root">
        <Chip
          label={status?.name || selectedStatus}
          className={`status-cell ${selectedStatus}`}
          aria-label={`Change status for ${school.school_title}`}
          disabled={loading}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        />
        {loading && (
          <Spinner
            animation="grow"
            role="status"
            className="loading-ripple"
            size="sm"
          />
        )}
      </div>
      {error && (
        <div role="alert" className="text-danger">
          {error}
        </div>
      )}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem disabled className="title">
          <b>Set {isCollege ? 'College' : 'School'} Status</b>
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === selectedStatus}
            onClick={() => handleMenuItemClick(option.id)}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
